import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import {
  AppBar,
  Box,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  styled,
  useTheme
} from '@mui/material';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider as MuiDivider } from '../components/coreComponents/divider';
import { useIdentity } from '../identityContext';
import { getParentSections } from './navigationBar';

type TopBarVariant = 'default' | 'modal' | 'brandModal';

interface _TopBarProps {
  variant: TopBarVariant;
  actions?: ReactNode;
}

interface Default extends _TopBarProps {
  variant: 'default';
  desktopLogo: ReactNode;
  mobileLogo: ReactNode;
}

interface Modal extends _TopBarProps {
  variant: 'modal';
  title: string;
  modalAction: ReactNode;
  hasBottomBorder?: boolean;
}

interface BrandModal extends _TopBarProps {
  variant: 'brandModal';
  desktopLogo: ReactNode;
  mobileLogo: ReactNode;
  modalAction: ReactNode;
  disableDivider?: boolean;
}

export type TopBarProps = Default | Modal | BrandModal;

const expandedNavBarWidth = 280;
const minNavBarWidth = 80;
// todo: this needs to be split into multiple components so it can be composed!
export function TopBar({
  position = 'fixed',
  disableDivider = false,
  ...props
}: TopBarProps & {
  position?: 'fixed' | 'sticky';
  disableDivider?: boolean;
  isNavBarExpanded: boolean;
  setIsNavBarExpanded: (value: boolean) => void;
  activeSection: string | null;
  setActiveSection: (value: string | null) => void;
  identity: Pick<ReturnType<typeof useIdentity>, 'organisationRoleActive'>;
}) {
  const theme = useTheme();
  return (
    <AppBar
      position={position}
      sx={{
        py: { xs: 1.5, sm: 2, md: 3 },
        px: { xs: 3, sm: 5, md: 8 },
        borderTop: theme => `3px solid ${theme.brandPalette.foreground.accent}`,
        borderBottom: theme =>
          props.variant === 'default' || (props.variant === 'modal' && props.hasBottomBorder)
            ? `1px solid ${theme.brandPalette.border.neutralFaded}`
            : undefined,
        transition: theme.transitions.create(['padding', 'background-color'], {
          duration: theme.transitions.duration.shortest
        })
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: { xs: 56, sm: 64, md: 72 },
          px: { xs: 2, sm: 3, md: 0 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 3, sm: 4, md: 7 }}
            sx={{
              ml: {
                md: props.isNavBarExpanded ? `${expandedNavBarWidth}px` : `${minNavBarWidth}px`
              },
              px: props.isNavBarExpanded ? { xs: 4, md: 8 } : { xs: 0, md: 0 },
              transition: 'margin-left 0.3s ease'
            }}
            divider={disableDivider ? null : <Divider orientation="vertical" flexItem />}
          >
            {(props.variant === 'modal' || props.variant === 'brandModal') && (
              <>{props.modalAction}</>
            )}
            <Left {...props} />
          </Stack>

          <Box sx={{ ml: 'auto' }}>
            <Right actions={props.actions} />
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

function Left({
  isNavBarExpanded,
  setIsNavBarExpanded,
  setActiveSection,
  activeSection,
  ...props
}: TopBarProps & {
  isNavBarExpanded: boolean;
  setIsNavBarExpanded: (value: boolean) => void;
  activeSection: string | null;
  setActiveSection: (value: string | null) => void;
  identity: Pick<ReturnType<typeof useIdentity>, 'organisationRoleActive'>;
}) {
  const { t: translation } = useTranslation();
  const identity = useIdentity();
  const organisationIds = identity.organisationRoles.map(role => role.organisation.value);

  if (props.variant === 'modal') {
    return (
      <>
        <Typography variant="labelLarge" sx={{ display: { xs: 'block', md: 'none' } }}>
          {props.title}
        </Typography>
        <Typography variant="h3" sx={{ display: { xs: 'none', md: 'block' } }}>
          {props.title}
        </Typography>
      </>
    );
  }

  if (props.variant === 'default' || props.variant === 'brandModal') {
    const isSettings = activeSection === 'settings';
    const currentSection = getParentSections({ translation, organisationIds, identity }).find(
      section => section.key === activeSection
    );
    const hasChildren = isSettings || currentSection?.children.length !== 0;
    if (
      !getParentSections({ translation, organisationIds, identity }).some(
        section => (section.children?.length ?? 0) > 0
      )
    )
      return null;

    if (!(activeSection && hasChildren))
      return <Stack direction="row" gap={{ xs: 3, md: 7 }} alignItems="center" />;

    return (
      <Stack direction="row" gap={{ xs: 3, sm: 4, md: 7 }} alignItems="center">
        <Box sx={{ display: { xs: 'none', sm: 'none', md: 'flex' } }}>
          <IconButton
            disableRipple
            disableTouchRipple
            aria-label={isNavBarExpanded ? 'Close submenu' : 'Open submenu'}
            aria-pressed={isNavBarExpanded}
            onClick={() => setIsNavBarExpanded(!isNavBarExpanded)}
            sx={{
              color: theme => theme.brandPalette.background.inverse,
              p: { xs: 1, sm: 1.5, md: 2 },
              '&:hover': { backgroundColor: 'unset' },
              '& svg': {
                fontSize: { xs: 22, sm: 24, md: 26 }
              }
            }}
          >
            {isNavBarExpanded ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
        </Box>
      </Stack>
    );
  }

  return null;
}

function Right({ actions }: { actions?: ReactNode }) {
  if (actions == null) return;

  return <>{actions}</>;
}

const Divider = styled(MuiDivider)(() => ({
  borderBottomWidth: '1px',
  margin: '0px !important'
}));
